import { useNavigate } from "@/lib/page-rendering"
import { Pencil } from "lucide-react"
import { useId, useState } from "react"
import { useTranslation } from "react-i18next"
import * as z from "zod"

import { ActionButton, SaveButton } from "@/components/shared/action-buttons"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { FormRenderer } from "@/lib/data-driven-forms/renderer"
import { DataDrivenFormSchema } from "@/lib/data-driven-forms/schema"
import { VALIDATION_ERROR_KEYS } from "@/i18n"
import type SearchShortlistsShortlistWorkspace from "@/types/serializers/SearchShortlistsShortlistWorkspace"

const formSchema: DataDrivenFormSchema<"shortlists"> = {
  type: "form",
  validator: z.object({
    name: z.string().trim().min(1, VALIDATION_ERROR_KEYS.REQUIRED),
  }),
  namespace: "shortlists",
  properties: [
    {
      type: "field:input",
      name: "name",
      label: "form.name.label",
      inputType: "text",
    },
  ],
}

/**
 * Renames a shortlist from a workspace tab without leaving its results. The
 * server returns to the referring tab, so the dialog only has to close once
 * the rename succeeds; a blank name never leaves the dialog.
 */
export function ShortlistRenameDialog({
  shortlist,
}: {
  shortlist: SearchShortlistsShortlistWorkspace
}) {
  const { t } = useTranslation("shortlists")
  const [open, setOpen] = useState(false)
  const formId = useId()
  const navigate = useNavigate()

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {/* The header also holds the back button and the shortlist's name, so
            below `sm` the action keeps its icon and hands the label to screen
            readers rather than squeezing the name. */}
        <ActionButton Icon={Pencil} className="max-sm:px-3">
          <span className="sr-only sm:not-sr-only">
            {t("workspace.rename")}
          </span>
        </ActionButton>
      </DialogTrigger>
      <DialogContent
        aria-describedby={undefined}
        data-testid="shortlist-rename-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t("workspace.rename_dialog.title")}</DialogTitle>
        </DialogHeader>
        <FormRenderer
          schema={formSchema}
          defaultValues={{ name: shortlist.name }}
          formId={formId}
          onSubmit={async (data) => {
            const { errors } = await navigate({
              href: `/search/shortlists/${shortlist.id}`,
              method: "patch",
              data: { shortlist: { name: data.name } },
            })
            if (Object.keys(errors).length === 0) setOpen(false)
          }}
        />
        <DialogFooter>
          <SaveButton form={formId} variant="default" />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
